import { AuthContext } from '@/contexts/AuthContext';
import CustomCheckoutModal from '@/modals/CustomCheckoutModal';
import PaymentLinkModal from '@/modals/PaymentLinkModal';
import { classNames } from '@/utils';
import { PlanGroupId, PlanTier, PlanType } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { PlanComparisonMap } from '@superfunnel/utils';
import { MoreVert } from 'iconoir-react';
import { useContext, useState } from 'react';

interface PlanCardProps {
  groupId: PlanGroupId;
  groupName: string;
  description: string;
  tiers: PlanTier[];
  selectedTierId: PlanType;
  features: string[];
  billingPeriod: 'monthly' | 'annual';
  isFeatured?: boolean;
  isLoading?: boolean;
  onWhite?: boolean;
  changePlansMap: PlanComparisonMap;
  onSelectPlan: (planId: PlanType) => void;
  onTierChange: (groupId: string, tierId: PlanType) => void;
  onManagePlan?: () => void;
}

// SVG Components
const CheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="relative h-2.5 w-2.5 shrink-0 grow-0"
    preserveAspectRatio="xMidYMid meet"
  >
    <path
      d="M2.08325 5.41699L3.74992 7.08366L7.91659 2.91699"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RadioSelected = () => (
  <div className="relative flex h-4 w-4 shrink-0 grow-0 items-center justify-center gap-2.5 rounded-[999px] bg-[#795dfc] p-0.5">
    <svg
      width="7"
      height="7"
      viewBox="0 0 7 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0 grow-0"
      preserveAspectRatio="xMidYMid meet"
    >
      <circle cx="3.5" cy="3.5" r="3.5" fill="white" />
    </svg>
  </div>
);

const RadioUnselected = () => (
  <div className="flex h-4 w-4 shrink-0 grow-0 items-center justify-start gap-2.5 rounded-[999px] border border-[#ced2da] bg-white p-0.5" />
);

export default function PlanCard({
  groupId,
  groupName,
  tiers,
  selectedTierId,
  features,
  billingPeriod,
  isFeatured = false,
  isLoading = false,
  onWhite = false,
  changePlansMap,
  onSelectPlan,
  onTierChange,
  onManagePlan,
}: PlanCardProps) {
  const { isSupportAdmin } = useContext(AuthContext);
  const [customCheckoutOpen, setCustomCheckoutOpen] = useState(false);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);

  const isCustomPlan = groupId === 'custom' || tiers.length === 0;
  const selectedTier = tiers.find((t) => t.id === selectedTierId) || tiers[0];
  const planRelationship = changePlansMap[selectedTierId];

  const handleOpenPaymentLinkModal = () => {
    if (!selectedTier?.variantId) return;
    setPaymentLinkModalOpen(true);
  };

  return (
    <div
      className={classNames(
        'relative flex max-w-[404px] grow flex-col items-start justify-start gap-3.5 overflow-hidden rounded-3xl bg-white p-4 2xl:p-8',
        onWhite
          ? 'border border-gray-200 shadow-none'
          : 'shadow-[0_4px_16px_rgba(121,93,252,0.12)]',
      )}
    >
      {/* Hidden admin menu - only renders for support admins */}
      {isSupportAdmin && !isCustomPlan && (
        <>
          <AdminMenu
            onOpenPaymentLinkModal={handleOpenPaymentLinkModal}
            onOpenCustomCheckout={() => setCustomCheckoutOpen(true)}
          />
          <PaymentLinkModal
            open={paymentLinkModalOpen}
            onClose={() => setPaymentLinkModalOpen(false)}
            variantId={selectedTier?.variantId || ''}
          />
          <CustomCheckoutModal
            open={customCheckoutOpen}
            onClose={() => setCustomCheckoutOpen(false)}
          />
        </>
      )}

      {isFeatured && (
        <div className="absolute top-6 -right-12 flex w-40 shrink-0 grow-0 rotate-45 items-center justify-center gap-2.5 bg-[#795dfc] py-0.5">
          <p className="shrink-0 grow-0 text-left text-xs font-medium text-white">
            Popular
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col items-start justify-start gap-2 self-stretch">
        <div className="flex flex-col items-start justify-start gap-2.5 self-stretch">
          <p className="text-[26px] font-medium text-[#141c25]">{groupName}</p>
        </div>
        {/* <p className="self-stretch text-base text-[#414e62]">{description}</p> */}
      </div>

      {/* Pricing Display */}
      <div className="flex items-baseline justify-start gap-1">
        {isCustomPlan ? (
          <p className="text-[40px] font-normal text-[#141c25]">Custom</p>
        ) : (
          <>
            <p className="text-[40px] font-normal text-[#141c25]">
              ${selectedTier?.monthlyPrice}
            </p>
            <p className="text-base text-[#414e62]">
              / {billingPeriod === 'annual' ? 'year' : 'month'}
            </p>
          </>
        )}
      </div>

      {/* Tier Options */}
      <div className="flex min-h-[148px] flex-col items-start justify-start gap-2 self-stretch">
        {isCustomPlan ? (
          <p className="text-sm text-[#414e62]">
            Contact us for custom pricing tailored to your needs.
          </p>
        ) : (
          tiers.map((tier) => {
            const isSelected = tier.id === selectedTierId;
            const tierPrice =
              billingPeriod === 'annual' && tier.yearlyPrice
                ? tier.yearlyPrice
                : tier.monthlyPrice;

            return (
              <button
                key={tier.id}
                onClick={() => onTierChange(groupId, tier.id)}
                className="flex w-full flex-col items-start justify-center gap-2.5 rounded-xl border border-[#f2f4f7] px-3 py-[11px]"
              >
                <div className="flex items-center justify-start gap-2">
                  {isSelected ? <RadioSelected /> : <RadioUnselected />}
                  <p
                    className={`text-sm ${isSelected ? 'text-[#141c25]' : 'text-[#414e62]'}`}
                  >
                    {tier.visitors.toLocaleString()} visitors – ${tierPrice}/
                    {billingPeriod === 'annual' ? 'year' : 'month'}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* CTA Button */}
      {isCustomPlan ? (
        <a
          href="https://calendly.com/superfunnelai/sales"
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-12 items-center justify-center gap-2.5 self-stretch rounded-xl bg-[#795dfc] px-5 py-[11px] hover:bg-[#6b4eeb]"
        >
          <p className="text-base font-medium text-white">Contact Sales</p>
        </a>
      ) : planRelationship === 'current' ? (
        <button
          onClick={onManagePlan}
          className="flex h-12 items-center justify-center gap-2.5 self-stretch rounded-xl bg-[#16bc61] px-5 py-[11px] hover:bg-[#14a855]"
        >
          <p className="text-base font-medium text-white">Manage Plan</p>
        </button>
      ) : planRelationship === 'downgrade' ? (
        <button
          onClick={() => onSelectPlan(selectedTierId)}
          disabled={isLoading}
          className="flex h-12 items-center justify-center gap-2.5 self-stretch rounded-xl border border-[#795dfc] bg-white px-5 py-[11px] hover:bg-[#f9f8ff] disabled:opacity-50"
        >
          <p className="text-base font-medium text-[#795dfc]">
            {isLoading ? 'Loading...' : 'Downgrade'}
          </p>
        </button>
      ) : (
        <button
          onClick={() => onSelectPlan(selectedTierId)}
          disabled={isLoading}
          className="flex h-12 items-center justify-center gap-2.5 self-stretch rounded-xl bg-[#795dfc] px-5 py-[11px] hover:bg-[#6b4eeb] disabled:opacity-50"
        >
          <p className="text-base font-medium text-white">
            {isLoading
              ? 'Loading...'
              : planRelationship === 'upgrade'
                ? 'Upgrade'
                : 'Start Optimizing Funnels'}
          </p>
        </button>
      )}

      {/* Usage Limits */}
      <div className="flex flex-col items-start justify-start gap-2 pt-1.5">
        <p className="w-[356px] text-sm font-semibold text-[#141c25]">
          Usage Limits:
        </p>
        <div className="flex w-[356px] flex-col items-start justify-start gap-2">
          {isCustomPlan ? (
            <>
              <p className="text-sm text-[#344051]">Unlimited pages</p>
              <p className="text-sm text-[#344051]">
                Unlimited visitors/sessions
              </p>
              <p className="text-sm text-[#344051]">Unlimited members</p>
            </>
          ) : (
            <>
              <p className="text-sm text-[#344051]">
                {selectedTier.limits.publishedPages === null
                  ? 'Unlimited'
                  : selectedTier.limits.publishedPages}{' '}
                pages
              </p>
              <p className="text-sm text-[#344051]">
                {selectedTier.visitors.toLocaleString()} visitors/sessions
              </p>
              <p className="text-sm text-[#344051]">
                {selectedTier.limits.members === null
                  ? 'Unlimited'
                  : selectedTier.limits.members}{' '}
                members
              </p>
            </>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="flex flex-col items-start justify-start gap-2 pt-1.5">
        <p className="w-[356px] text-sm font-semibold text-[#141c25]">
          Features:
        </p>
        <div className="flex w-[356px] flex-col items-start justify-start gap-2">
          {features.map((feature) => (
            <div
              key={feature}
              className="flex items-center justify-start gap-2"
            >
              <div className="flex h-4 w-4 items-center justify-center rounded-[999px] bg-[#16bc61]">
                <CheckIcon />
              </div>
              <p className="text-sm text-[#344051]">{feature}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface AdminMenuProps {
  onOpenPaymentLinkModal: () => void;
  onOpenCustomCheckout: () => void;
}

const AdminMenu = ({
  onOpenPaymentLinkModal,
  onOpenCustomCheckout,
}: AdminMenuProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="group/admin absolute top-2 left-2 z-10">
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            size={'icon'}
            type={'button'}
            variant={'outline'}
            className={classNames(
              'rounded-2lg',
              menuOpen ? 'visible' : 'invisible group-hover/admin:visible',
            )}
          >
            <MoreVert
              strokeWidth={3}
              className="h-4 w-4"
              width={20}
              height={20}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start">
          <DropdownMenuItem onClick={onOpenPaymentLinkModal}>
            Copy payment link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenCustomCheckout}>
            Custom checkout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
